/**
 * "Watch live": turn the link an organizer pastes into something a spectator page can embed.
 * YouTube and Twitch are the two that stream from a phone or OBS today; anything else is left
 * as a plain link that opens in a new tab.
 */
export type WatchEmbed =
    | {kind: 'youtube'; embedUrl: string; watchUrl: string}
    | {kind: 'twitch'; embedUrl: string; watchUrl: string}
    | {kind: 'link'; embedUrl: null; watchUrl: string};

const YT_ID = /^[A-Za-z0-9_-]{6,}$/;

export function parseWatchUrl(raw: string | null | undefined, parentHost = 'scorepal.co'): WatchEmbed | null {
    if (!raw || !raw.trim()) return null;
    let url: URL;
    try {
        url = new URL(raw.trim());
    } catch {
        return null;
    }
    const host = url.hostname.replace(/^www\.|^m\./, '');
    const watchUrl = url.toString();

    if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host === 'youtu.be') {
        let id: string | null = null;
        if (host === 'youtu.be') id = url.pathname.slice(1).split('/')[0];
        else if (url.pathname === '/watch') id = url.searchParams.get('v');
        else {
            const m = url.pathname.match(/^\/(?:live|embed|shorts)\/([^/]+)/);
            if (m) id = m[1];
        }
        if (id && YT_ID.test(id)) {
            return {kind: 'youtube', embedUrl: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`, watchUrl};
        }
        // A channel's /live page or anything else on YouTube: open it, do not embed it.
        return {kind: 'link', embedUrl: null, watchUrl};
    }

    if (host === 'twitch.tv' || host === 'player.twitch.tv') {
        const channel = host === 'player.twitch.tv' ? url.searchParams.get('channel') : url.pathname.slice(1).split('/')[0];
        if (channel && /^[A-Za-z0-9_]{3,}$/.test(channel)) {
            // Twitch refuses to embed without the hosting page's domain in `parent`.
            return {kind: 'twitch', embedUrl: `https://player.twitch.tv/?channel=${encodeURIComponent(channel)}&parent=${encodeURIComponent(parentHost)}&autoplay=true`, watchUrl};
        }
        return {kind: 'link', embedUrl: null, watchUrl};
    }

    return {kind: 'link', embedUrl: null, watchUrl};
}

export const WATCH_HINT = 'Paste your YouTube video or live link (youtube.com/watch?v=… or youtu.be/…) or a Twitch channel. Spectators get a Watch button on the live page.';
